import { waitForElem } from "./helpers";
// import { scroll } from "./scroll-anims";

export const initHeader = () => {
	initMenuItemEvents();
	initResizeEvents();
	addDropdownButtonEvents();
	initDetermineLogoColor();
};

let isMobile = window.innerWidth < 1024;
const mouseEnterHandlers = new WeakMap();
const mouseLeaveHandlers = new WeakMap();

const initMenuItemEvents = () => {
	if (!document.querySelectorAll("#primary-navigation .menu a")) return;
	updateResponsiveNav(true); // Ensure initial setup
};

const updateResponsiveNav = (initialLoad = false) => {
	const nav = document.querySelector("#primary-navigation");
	const navModal = document.querySelector("#navigation-modal .menu-cell");
	const navContainer = document.querySelector(".menu-container");

	if (window.innerWidth < 1024) {
		if (initialLoad || !isMobile) {
			isMobile = true;
			navModal.appendChild(nav);
			setTimeout(() => {
				removeMenuItemHoverEvents();
			}, 250);
		}
	} else {
		if (initialLoad || isMobile) {
			isMobile = false;
			navContainer.appendChild(nav);
			setTimeout(() => {
				addMenuItemHoverEvents();
			}, 250);
		} else if (initialLoad) {
			// Initially, if not mobile, add hover events directly
			addMenuItemHoverEvents();
		}
	}
};

const debounce = (func, wait, immediate) => {
	let timeout;
	return function () {
		const context = this,
			args = arguments;
		const later = function () {
			timeout = null;
			if (!immediate) func.apply(context, args);
		};
		const callNow = immediate && !timeout;
		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
		if (callNow) func.apply(context, args);
	};
};

const handleHover = debounce((menuItems, item) => {
	const submenu = item.nextElementSibling;

	if (submenu && submenu.matches(".sub-menu")) {
		// Reset alignment classes
		submenu.classList.remove("right-aligned");

		// Calculate space and re-apply alignment class if needed
		const rect = submenu.getBoundingClientRect();
		const spaceRight = window.innerWidth - rect.right;
		const submenuWidth = submenu.offsetWidth;

		if (spaceRight + 150 < submenuWidth) {
			submenu.classList.add("right-aligned");
		} else {
			submenu.classList.remove("right-aligned");
		}

		// Handle second-level submenu alignment
		const secondLevelSubmenus = submenu.querySelectorAll(".sub-menu");
		secondLevelSubmenus.forEach((subSubmenu) => {
			subSubmenu.classList.remove("right-aligned");

			const subSubmenuRect = subSubmenu.getBoundingClientRect();
			const subSubmenuSpaceRight = window.innerWidth - subSubmenuRect.right;
			const subSubmenuWidth = subSubmenu.offsetWidth;

			if (subSubmenuSpaceRight < subSubmenuWidth) {
				subSubmenu.classList.add("right-aligned");
			} else {
				subSubmenu.classList.remove("right-aligned");
			}
		});
	}

	addActiveClass(menuItems, item);
}, 100);

const addActiveClass = (menuItems, item) => {
	menuItems.forEach((otherItem) => {
		if (otherItem !== item) {
			otherItem.classList.remove("active");
			const otherSubmenu = otherItem.nextElementSibling;
			if (otherSubmenu && otherSubmenu.matches(".sub-menu")) {
				otherSubmenu.classList.remove("active");
			}
			const parentColumn = otherItem?.closest(".column");
			if (parentColumn) {
				parentColumn.classList.remove("active");
			}
		}
	});

	item.classList.add("active");
	item.closest(".menu-item-has-children")?.classList.add("active");
	const parentColumn = item?.closest(".column");
	if (parentColumn) {
		parentColumn.classList.add("active");
	}
	const submenu = item.nextElementSibling;
	if (submenu && submenu.matches(".sub-menu")) {
		submenu.classList.add("active");
	}
};

// Event handler functions
const handleMouseEnter = (menuItems, item) => () => handleHover(menuItems, item);

const handleMouseLeave = (item, submenu) => () => {
	setTimeout(() => {
		const notHoveringSubNav = !submenu || !submenu.matches(".sub-menu:hover");
		const notHoveringSubItem = submenu && submenu.querySelectorAll("a:hover").length === 0;
		if (!item.matches(":hover") && notHoveringSubNav && notHoveringSubItem) {
			item.classList.remove("active");
			item.closest(".menu-item-has-children")?.classList.remove("active");
			if (submenu) {
				submenu.classList.remove("active");
			}
		}
	}, 400);
};

const addMenuItemHoverEvents = () => {
	const menuItems = document.querySelectorAll("#primary-navigation .menu a");

	menuItems.forEach((item) => {
		const submenu = item.nextElementSibling;

		const mouseEnterHandler = handleMouseEnter(menuItems, item);
		const mouseLeaveHandler = handleMouseLeave(item, submenu);

		item.addEventListener("mouseenter", mouseEnterHandler);
		item.addEventListener("mouseleave", mouseLeaveHandler);

		if (submenu) {
			submenu.addEventListener("mouseleave", mouseLeaveHandler);
		}

		mouseEnterHandlers.set(item, mouseEnterHandler);
		mouseLeaveHandlers.set(item, mouseLeaveHandler);
	});
};

const removeMenuItemHoverEvents = () => {
	const menuItems = document.querySelectorAll("#primary-navigation .menu a");

	menuItems.forEach((item) => {
		const submenu = item.nextElementSibling;
		const mouseEnterHandler = mouseEnterHandlers.get(item);
		const mouseLeaveHandler = mouseLeaveHandlers.get(item);

		if (mouseEnterHandler) {
			item.removeEventListener("mouseenter", mouseEnterHandler);
		}
		if (mouseLeaveHandler) {
			item.removeEventListener("mouseleave", mouseLeaveHandler);
		}
		if (submenu) {
			submenu.removeEventListener("mouseleave", mouseLeaveHandler);
		}
	});
};

const addDropdownButtonEvents = () => {
	const menuItems = document.querySelectorAll("#primary-navigation .menu a");

	menuItems.forEach((item) => {
		const submenu = item.nextElementSibling;
		const dropdownButton = item.querySelector(".icon-caret-down");

		if (dropdownButton) {
			dropdownButton.addEventListener("click", (event) => {
				event.preventDefault();
				toggleSubMenu(item, submenu);
			});
		}

		function toggleSubMenu(item, submenu) {
			if (submenu) {
				submenu.classList.toggle("active");
				item.classList.toggle("active");
				let parent = item.parentElement;
				while (parent && parent.tagName !== "LI") {
					parent = parent.parentElement;
				}
				if (parent) {
					parent.classList.toggle("active");
				}
			}
		}
	});
};

const initResizeEvents = () => {
	const handleResize = () => {
		updateResponsiveNav();
	};

	window.addEventListener("resize", handleResize);
};

const initDetermineLogoColor = async () => {
	if (!document.querySelector(".site-header .logo svg")) return;

	// get the color value directly behind the logo in the header
	const logo = document.querySelector(".site-header .logo");

	function getColorBehindLogo(logoSelector = ".site-header .logo") {
		const logo = document.querySelector(logoSelector);
		if (!logo) return "rgb(255,255,255)";

		const rect = logo.getBoundingClientRect();
		const x = rect.left + rect.width / 2;
		const y = rect.top + rect.height / 2;

		let elem = document.elementFromPoint(x, y);

		while (elem && elem !== document.body) {
			const style = window.getComputedStyle(elem);
			const bgColor = style.backgroundColor;
			const bgImage = style.backgroundImage;

			if (bgColor && bgColor !== "rgba(0, 0, 0, 0)" && bgColor !== "transparent") {
				return bgColor;
			}
			if (bgImage && bgImage !== "none") {
				// Optionally handle background images here
				// For now, fallback or log
				console.warn("Background image detected behind logo, returning default color.");
				return "rgb(255,255,255)";
			}
			elem = elem.parentElement;
		}
		return "rgb(255,255,255)";
	}
	console.log("getColorBehindLogo", getColorBehindLogo());
};
